import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Buffer Test Page - Preview fortification graphics
 * Route: /buffer
 * 
 * Allows testing of sandbags, trenches, and barbed wire fortifications
 * at different buffer levels without playing the game
 */

// Standalone Landmark Preview Component (doesn't use absolute positioning)
function LandmarkPreview({ country, initialPopulation }) {
  const currentPopulation = country.resources?.population || 0;
  
  // Calculate buffer
  const bufferPopulation = Math.max(0, currentPopulation - initialPopulation);
  const bufferPercent = initialPopulation > 0 ? (bufferPopulation / initialPopulation) * 100 : 0;
  
  // Calculate damage
  const populationPercent = initialPopulation > 0 ? Math.min(100, (currentPopulation / initialPopulation) * 100) : 100;
  
  const hasBuffer = bufferPercent > 0;
  
  // Fortification levels
  let fortificationLevel = 0;
  if (bufferPercent > 60) {
    fortificationLevel = 3; // Heavy
  } else if (bufferPercent > 30) {
    fortificationLevel = 2; // Medium
  } else if (bufferPercent > 0) {
    fortificationLevel = 1; // Light
  }
  
  // Damage stage
  let stage = 0;
  if (!hasBuffer) {
    if (populationPercent < 30) {
      stage = 2;
    } else if (populationPercent < 70) {
      stage = 1;
    }
  }
  
  // Landmark data
  const landmarks = {
    USA: {
      name: "U.S. Capitol",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/7qdvyva1_Capitol-whole.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/42d03rdp_Capitol-half.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/bmjbulce_Capitol-gone.png'
      ]
    },
    RUS: {
      name: "Spasskaya Tower",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/8oxw0s33_Spasskaya-whole.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ydes942z_Spasskaya-half.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/frtq98rv_Spasskaya-gone.png'
      ]
    },
    CHN: {
      name: "Tiananmen Gate",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/nsb4u0vc_Tiananmen%20Gate%20-%20whole.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/rizuvks2_Tiananmen%20Gate%20-%20half.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/i76147dw_Tiananmen%20Gate%20-%20gone.png'
      ]
    },
    NK: {
      name: "Juche Tower",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/uqnc6prg_Juche%20Tower%20-%20whole.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/5n3wauud_Juche%20Tower%20-%20half.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/5a5v1q1h_Juche%20Tower%20-%20gone.png'
      ]
    },
    EU: {
      name: "Berlaymont Building",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/mwqyd0eh_Berlaymont%20-%20whole.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/60y6yc3a_Berlaymont%20-%20half.png',
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/xpa92000_Berlaymont%20-%20gone.png'
      ]
    }
  };

  const landmarkData = landmarks[country.code];
  if (!landmarkData) return null;

  const imageUrl = landmarkData.images[stage];
  const fortificationLabels = ['No Defenses', 'Light Fortifications', 'Medium Fortifications', 'Heavy Fortifications'];
  const fortificationColors = ['text-gray-400', 'text-yellow-400', 'text-orange-400', 'text-red-400'];

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Fortifications layer */}
      {hasBuffer && (
        <div className="absolute" style={{ width: '140px', height: '140px' }}>
          {/* Sandbags (Level 1+) */}
          {fortificationLevel >= 1 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-1">
              {[...Array(7)].map((_, i) => (
                <div
                  key={`sandbag-${i}`}
                  className="w-4 h-3 rounded-sm"
                  style={{
                    background: 'linear-gradient(135deg, #8B7355 0%, #6B5A45 50%, #4A3F35 100%)',
                    border: '1px solid rgba(60, 50, 40, 0.8)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.5)'
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Trenches (Level 2+) */}
          {fortificationLevel >= 2 && (
            <div className="absolute bottom-6 left-0 right-0">
              <svg width="100%" height="25">
                <path
                  d="M 10 18 Q 35 12, 60 18 T 110 18"
                  stroke="#3D2E1F"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray="4,3"
                />
                <path
                  d="M 15 14 Q 40 9, 65 14 T 115 14"
                  stroke="#2D1E0F"
                  strokeWidth="3"
                  fill="none"
                  opacity="0.6"
                />
              </svg>
            </div>
          )}
          
          {/* Barbed Wire (Level 3) */}
          {fortificationLevel >= 3 && (
            <div className="absolute top-2 left-0 right-0">
              <svg width="100%" height="20">
                <path
                  d="M 5 10 L 15 8 L 25 12 L 35 7 L 45 11 L 55 9 L 65 12 L 75 8 L 85 11 L 95 9 L 105 11 L 115 10 L 125 12 L 135 9"
                  stroke="#6B7280"
                  strokeWidth="2"
                  fill="none"
                />
                {[15, 25, 35, 45, 55, 65, 75, 85, 95, 105, 115, 125].map((x, i) => (
                  <circle
                    key={`barb-${i}`}
                    cx={x}
                    cy={i % 2 === 0 ? 8 : 12}
                    r="2"
                    fill="#9CA3AF"
                  />
                ))}
              </svg>
            </div>
          )}
          
          {/* Glow effect */}
          <div
            className="absolute inset-0 rounded-full opacity-20 blur-lg"
            style={{
              background: fortificationLevel === 3 
                ? 'radial-gradient(circle, rgba(239, 68, 68, 0.4) 0%, transparent 70%)'
                : fortificationLevel === 2
                ? 'radial-gradient(circle, rgba(249, 115, 22, 0.4) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(234, 179, 8, 0.4) 0%, transparent 70%)'
            }}
          />
        </div>
      )}
      
      {/* Landmark Image */}
      <img
        src={imageUrl}
        alt={landmarkData.name}
        className="relative z-10"
        style={{
          width: '120px',
          height: '120px',
          objectFit: 'contain',
          filter: stage === 2 
            ? 'grayscale(0.8) brightness(0.7) drop-shadow(0 0 20px rgba(239, 68, 68, 0.8))' 
            : stage === 1 
            ? 'grayscale(0.3) brightness(0.85) drop-shadow(0 0 15px rgba(249, 115, 22, 0.7))' 
            : 'drop-shadow(0 0 15px rgba(34, 197, 94, 0.6))',
        }}
      />
      
      {/* Label */}
      <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-20">
        <div className="bg-black/90 px-3 py-1.5 rounded backdrop-blur-sm"
             style={{
               border: hasBuffer
                 ? `1px solid ${fortificationLevel === 3 ? 'rgba(239, 68, 68, 0.6)' : fortificationLevel === 2 ? 'rgba(249, 115, 22, 0.6)' : 'rgba(234, 179, 8, 0.6)'}`
                 : '1px solid rgba(100, 116, 139, 0.5)',
               boxShadow: hasBuffer
                 ? (fortificationLevel === 3 ? '0 0 12px rgba(239, 68, 68, 0.4)' : fortificationLevel === 2 ? '0 0 12px rgba(249, 115, 22, 0.4)' : '0 0 12px rgba(234, 179, 8, 0.4)')
                 : '0 0 8px rgba(100, 116, 139, 0.3)'
             }}
        >
          <div className="text-xs font-bold text-gray-200 uppercase tracking-wide">
            {landmarkData.name}
          </div>
          {hasBuffer && (
            <div className={`text-[10px] font-bold ${fortificationColors[fortificationLevel]} uppercase tracking-wider mt-0.5`}>
              🛡️ {fortificationLabels[fortificationLevel]} (+{bufferPercent.toFixed(0)}%)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BufferTestPage() {
  const navigate = useNavigate();
  
  // Test countries with different buffer levels
  const [countries, setCountries] = useState([
    { code: 'USA', name: 'United States', initialPop: 300, bufferPercent: 0 },
    { code: 'RUS', name: 'Russia', initialPop: 144, bufferPercent: 0 },
    { code: 'CHN', name: 'China', initialPop: 1400, bufferPercent: 0 },
    { code: 'NK', name: 'North Korea', initialPop: 26, bufferPercent: 0 },
    { code: 'EU', name: 'European Union', initialPop: 450, bufferPercent: 0 }
  ]);

  const handleBufferChange = (countryCode, newBufferPercent) => {
    setCountries(prev => prev.map(c => 
      c.code === countryCode 
        ? { ...c, bufferPercent: newBufferPercent }
        : c
    ));
  };

  const presetBuffers = [
    { label: 'No Buffer', value: 0, color: 'bg-gray-600' },
    { label: 'Light (15%)', value: 15, color: 'bg-yellow-600' },
    { label: 'Medium (45%)', value: 45, color: 'bg-orange-600' },
    { label: 'Heavy (80%)', value: 80, color: 'bg-red-600' },
    { label: 'Max (150%)', value: 150, color: 'bg-purple-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={() => navigate('/')}
          className="mb-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          ← Back to Game
        </button>
        
        <h1 className="text-4xl font-bold text-white mb-2">
          🛡️ Fortification Test Lab
        </h1>
        <p className="text-gray-400 text-lg">
          Preview sandbags, trenches, and barbed wire at different buffer levels
        </p>
        
        <div className="mt-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
          <h2 className="text-white font-bold mb-2">📖 How It Works:</h2>
          <ul className="text-gray-300 space-y-1 text-sm">
            <li>• <span className="text-yellow-400">Light (0-30% buffer):</span> Sandbags only</li>
            <li>• <span className="text-orange-400">Medium (30-60% buffer):</span> Sandbags + Trenches</li>
            <li>• <span className="text-red-400">Heavy (60%+ buffer):</span> Sandbags + Trenches + Barbed Wire</li>
            <li>• <span className="text-purple-400">Buffer = (Current Pop - Initial Pop) / Initial Pop × 100%</span></li>
          </ul>
        </div>
      </div>

      {/* Quick Preset Buttons */}
      <div className="max-w-7xl mx-auto mb-8">
        <h3 className="text-white font-bold mb-3">⚡ Quick Presets (Apply to All):</h3>
        <div className="flex flex-wrap gap-3">
          {presetBuffers.map(preset => (
            <button
              key={preset.value}
              onClick={() => {
                setCountries(prev => prev.map(c => ({ ...c, bufferPercent: preset.value })));
              }}
              className={`${preset.color} hover:opacity-80 text-white font-bold px-6 py-3 rounded-lg transition-all transform hover:scale-105`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Country Test Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {countries.map(country => {
          const currentPop = country.initialPop * (1 + country.bufferPercent / 100);
          
          return (
            <div key={country.code} className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              {/* Country Header */}
              <div className="mb-4">
                <h2 className="text-xl font-bold text-white mb-1">{country.name}</h2>
                <p className="text-gray-400 text-sm">
                  Initial: {country.initialPop}M | Current: {currentPop.toFixed(0)}M
                </p>
              </div>

              {/* Landmark Preview (Standalone rendering) */}
              <div className="relative bg-slate-900 rounded-lg p-4 mb-4" style={{ height: '280px' }}>
                <LandmarkPreview
                  country={{
                    code: country.code,
                    resources: { population: currentPop }
                  }}
                  initialPopulation={country.initialPop}
                />
              </div>

              {/* Buffer Control */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-white font-semibold">Buffer Level:</label>
                  <span className={`text-lg font-bold ${
                    country.bufferPercent >= 60 ? 'text-red-400' :
                    country.bufferPercent >= 30 ? 'text-orange-400' :
                    country.bufferPercent > 0 ? 'text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {country.bufferPercent}%
                  </span>
                </div>
                
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="5"
                  value={country.bufferPercent}
                  onChange={(e) => handleBufferChange(country.code, parseInt(e.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-slate-700"
                  style={{
                    accentColor: country.bufferPercent >= 60 ? '#ef4444' :
                                 country.bufferPercent >= 30 ? '#f97316' :
                                 country.bufferPercent > 0 ? '#eab308' :
                                 '#6b7280'
                  }}
                />
                
                {/* Individual Preset Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleBufferChange(country.code, 15)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-bold py-1 px-2 rounded transition-colors"
                  >
                    Light
                  </button>
                  <button
                    onClick={() => handleBufferChange(country.code, 45)}
                    className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold py-1 px-2 rounded transition-colors"
                  >
                    Medium
                  </button>
                  <button
                    onClick={() => handleBufferChange(country.code, 80)}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1 px-2 rounded transition-colors"
                  >
                    Heavy
                  </button>
                </div>

                {/* Fortification Level Indicator */}
                <div className="pt-2 border-t border-slate-700">
                  <p className="text-xs text-gray-400">
                    {country.bufferPercent === 0 && '🏛️ No Fortifications'}
                    {country.bufferPercent > 0 && country.bufferPercent <= 30 && '🟡 Sandbags'}
                    {country.bufferPercent > 30 && country.bufferPercent <= 60 && '🟠 Sandbags + Trenches'}
                    {country.bufferPercent > 60 && '🔴 Full Defenses (Sandbags + Trenches + Wire)'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="max-w-7xl mx-auto mt-8 p-6 bg-slate-800 rounded-xl border border-slate-700">
        <h3 className="text-white font-bold mb-3">💡 In-Game Usage:</h3>
        <p className="text-gray-300 text-sm mb-2">
          During gameplay, fortifications automatically appear when a country's population exceeds its initial/starting population:
        </p>
        <ul className="text-gray-400 text-sm space-y-1 ml-4">
          <li>• If you start with 300M and reach 345M → 15% buffer → Light fortifications (sandbags)</li>
          <li>• If you reach 435M → 45% buffer → Medium fortifications (sandbags + trenches)</li>
          <li>• If you reach 540M → 80% buffer → Heavy fortifications (full defense system)</li>
          <li>• Fortifications absorb damage first - your landmark stays pristine!</li>
        </ul>
      </div>
    </div>
  );
}
